import { Component, OnInit } from '@angular/core';
import { JsonPipe, KeyValuePipe, Location, NgClass } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  SessionZipFilter,
  TracerService,
  completedAt,
  isNotFound,
} from '../../services/tracer.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { NgxSpinnerComponent, NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { finalize, firstValueFrom, timeout, TimeoutError } from 'rxjs';
import { Request, Session, Trace, Message, isInboundTraceSource } from '../../models/tracer.model';
import { FormatDatePipe } from '../../pipes/format-date-pipe';
import { GetProvidersPipe } from '../../pipes/get-providers-pipe';
import { GetMethodLabelPipe } from '../../pipes/get-method-label-pipe';
import { GetMethodColorPipe } from '../../pipes/get-method-color-pipe';
import { GetTracesBySourcePipe } from '../../pipes/get-traces-by-source-pipe';
import { GetTracesBySourceAndProvidersPipe } from '../../pipes/get-traces-by-source-and-providers-pipe';
import { GetTraceBorderColorPipe } from '../../pipes/get-trace-border-color-pipe';
import { GetStatusPipe } from '../../pipes/get-status-pipe';
import { GetSessionStatusPipe } from '../../pipes/get-session-status-pipe';
import { SessionLastActionPipe } from '../../pipes/session-last-action-pipe';
import { TraceUtilsService } from '../../services/trace-utils';
import { decodeMessageBody } from '../../services/decode-message-body';
import xmlFormat from 'xml-formatter';
import { runtimeConfig } from '../../runtime-config';
import { IsValidJsonPipe } from '../../pipes/is-valid-json-pipe';
import { NgxJsonViewerModule } from 'ngx-json-viewer';
import { ParseJsonPipe } from '../../pipes/parse-json-pipe';
import { Highlight } from 'ngx-highlightjs';
import { NormalizeFormatPipe } from '../../pipes/normalize-format-pipe';
import { JsonApiTraceTimePipe } from '../../pipes/json-api-trace-time-pipe';
import { OrderMessagesPipe } from '../../pipes/order-messages-pipe';
import { MessageTabLabelPipe } from '../../pipes/message-tab-label-pipe';
import { IsProviderCallbackPipe } from '../../pipes/is-provider-callback-pipe';
import { CollapseProvidersPipe } from '../../pipes/collapse-providers-pipe';
import qs from 'qs';
import { sniffBodyFormat } from '../../services/body-format';

// Names that identify the "request" half of a trace's message pair.
// Used by selectTrace to pick the message the user lands on first.
const REQUEST_NAMES = new Set(['input', 'provider_request']);

// BookingPad deep-link targets. A STATIC per-environment map — NOT
// runtimeConfig.bpUrl — because the "Relaunch in BP" dropdown links to
// every environment from any tracer instance. Localhost is handled
// separately (see bpLocalhostBase) because its port is operator-
// configurable and persisted per-browser.
const BP_REMOTE_TARGETS: { label: string; base: string }[] = [
  { label: 'Staging', base: 'https://web.bookingpad.work/' },
  { label: 'Sandbox', base: 'https://web.bookingpad.net/' },
  { label: 'Release', base: 'https://web.bookingpad.live/' },
  { label: 'Production', base: 'https://web.bookingpad.com/' },
];

// localStorage key + default for the local BookingPad dev port. BP is
// run alongside the tracer (which owns the Angular default :4200), so
// the historical default is :4201 — but a dev may run BP anywhere, so
// it is editable from the dropdown and persisted per-browser.
const BP_LOCAL_PORT_KEY = 'bpLocalPort';
const BP_LOCAL_PORT_DEFAULT = '4201';

@Component({
  selector: 'app-request-details',
  imports: [
    NgxSpinnerComponent,
    NgClass,
    FormatDatePipe,
    GetProvidersPipe,
    GetMethodLabelPipe,
    GetMethodColorPipe,
    RouterLink,
    GetTracesBySourcePipe,
    GetTracesBySourceAndProvidersPipe,
    GetTraceBorderColorPipe,
    GetStatusPipe,
    GetSessionStatusPipe,
    SessionLastActionPipe,
    KeyValuePipe,
    IsValidJsonPipe,
    NgxJsonViewerModule,
    ParseJsonPipe,
    Highlight,
    NormalizeFormatPipe,
    JsonApiTraceTimePipe,
    OrderMessagesPipe,
    MessageTabLabelPipe,
    IsProviderCallbackPipe,
    CollapseProvidersPipe,
  ],
  templateUrl: './request-details.html',
  styleUrl: './request-details.scss',
})
export class RequestDetails implements OnInit {
  private mbSize = 1024 * 1024;
  loaded: boolean = false;
  agRealm: string | null = null;
  query: string = '';
  session!: Session;
  requests: Request[] = [];
  providers: string[] = [];
  selectedReq!: Request;
  selectedTrace: Trace | null = null;
  selectedMessage: Message | null = null;
  uniqueTracesByProviders: Trace[] = [];
  body: string = '';
  // Original, un-reformatted message body as it was fetched, captured
  // alongside `body` BEFORE formattedXML/formattedJSON reindents it.
  // copyCurl uses this so the --data-raw payload matches the bytes that
  // were actually sent rather than the pretty-printed display version.
  rawBody: string = '';
  bodyFromPresignedURL: string = '';
  cachedBodies: any = {};
  headers: any = {};
  cachedHeaders: any = {};
  showHeaders: boolean = false;
  copiedHeaders: boolean = false;
  copiedBody: boolean = false;
  copiedCurl: boolean = false;
  fetchMessageDetailsError: string = '';
  fetchMessageDetailsWarning: string = '';
  showFetchMessageDetailsButton: boolean = false;
  showDownloadButton: boolean = true;
  selectedMessageExceeded: boolean = false;
  isMessageLoading: boolean = false;
  // File-size tier state populated by /message/:id response.
  // Three modes governed by the backend's two thresholds in
  // tracer/app/controllers/page_controller.go:
  //   inlineDisplayLimit   = 250 KB → body returned inline (Tier 1)
  //   onDemandDisplayLimit = 1   MB → presigned URL only, render
  //                                   "Show file (N KB)" button (Tier 2)
  //   above onDemandDisplayLimit   → zip_only=true, render
  //                                   "Available via session ZIP" notice (Tier 3)
  selectedMessageSize: number = 0;
  selectedMessageZipOnly: boolean = false;
  // Cached presigned URL for Tier 2. Populated when response carries
  // display_on_demand=true; consumed when the user clicks the
  // "Show file" button. Cleared on selectMessage().
  onDemandPresignedURL: string = '';
  // Presigned URL for Tier-3 (zip-only) files. Unlike onDemandPresignedURL
  // this does NOT surface a "Show file" button — the file is too large to
  // render inline — but the Download File button uses it to fetch the real
  // content on demand so the saved file isn't empty.
  zipOnlyDownloadURL: string = '';
  expandedGroups = new Set<number>();
  env: string = '';
  hash: string = '';

  // "Relaunch in BP" dropdown. Remote targets are static (see
  // BP_REMOTE_TARGETS); Localhost is rendered as its own row whose
  // port is operator-editable and persisted per-browser in
  // localStorage (bpLocalPort). relaunchPending guards against a
  // double click while the AirShopping input body is being fetched.
  bpTargets = BP_REMOTE_TARGETS;
  bpLocalPort: string = BP_LOCAL_PORT_DEFAULT;
  relaunchPending = false;

  // Guard against double-toggling the "important" star while an update
  // is in flight (changeIsImportant). Also drives the button's disabled
  // state in the template.
  importantPending = false;

  // Localized loading flag for the session-ZIP download so a stalled
  // backend no longer blocks the whole page behind the global spinner. Holds
  // the key of the link being downloaded (see zipKey), '' when idle.
  zipDownloading = '';

  // AI Evaluation state
  showEvaluationPanel: boolean = false;
  // Replay panel host. Admin-only; lazy-mounted so the plan fetch only
  // fires when the user actually clicks Replay.
  showReplayPanel: boolean = false;
  loadingEvaluation: boolean = false;
  evaluationError: string | null = null;

  // AI Ticket state — full lifecycle lives inside the panel; RequestDetails
  // just toggles visibility with showTicketPanel.
  showTicketPanel: boolean = false;

  // Compare-with-another-session modal. The modal handles its own
  // input + navigation; RequestDetails just owns the open/close flag.
  showDiffInputModal: boolean = false;

  // Deep-link state populated when the URL carries ?from=diff. Triggers
  // a banner above the JSON viewer and a small "side-by-side" panel
  // showing what the leaf looked like in A vs the other session.
  diffSourcePath: string = '';
  diffSourcePayload: string = '';
  diffOtherSession: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    protected location: Location,
    private tracerService: TracerService,
    private ls: LocalStorageService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private traceUtilsService: TraceUtilsService,
  ) {}

  ngOnInit(): void {
    this.agRealm = this.ls.getItem('agRealm');
    this.bpLocalPort = this.ls.getItem(BP_LOCAL_PORT_KEY) || BP_LOCAL_PORT_DEFAULT;

    this.route.queryParamMap.subscribe((params) => {
      this.query = params.get('q') ?? '';

      // Diff deep-link parameters. Captured here regardless of the
      // initial fetch path so the banner appears as soon as the page
      // hydrates with a known query.
      this.diffSourcePath = params.get('path') ?? '';
      this.diffSourcePayload = params.get('payload') ?? '';
      this.diffOtherSession = params.get('other') ?? '';

      if (!this.query) {
        this.router.navigateByUrl('/');
      } else {
        this.getSessionData(this.query);
      }
    });
  }

  getSessionData(query: string) {
    this.loaded = false;
    this.spinner.show();
    // A new search from the header reuses this component: drop the previous
    // result, or a search that finds nothing would keep showing it.
    this.requests = [];
    this.selectedReq = undefined!;

    this.tracerService
      .search(query)
      .pipe(
        finalize(() => {
          this.loaded = true;
          this.spinner.hide();
        }),
      )
      .subscribe({
        next: (res) => {
          // Surface the BE's error string FIRST when present — it
          // carries the most specific diagnostic ("No requests found
          // with that Request or Session ID", etc.). Previously this
          // branch was last and the generic "No requests found in
          // session data" toast fired instead, masking the real
          // reason. We also stay on /search now: redirecting back to
          // / lost the query in the URL, the user couldn't tell what
          // had happened, and the toastr disappeared after a few
          // seconds. Staying put leaves the spinner-finished empty
          // state in place so the URL and the message both remain
          // visible.
          if (!res || (typeof res === 'object' && Object.keys(res).length === 0)) {
            this.toastr.error('Session data is missing or empty', 'Error');
          } else if (res.error) {
            this.toastr.error(res.error, 'Error');
          } else if (!res.requests?.length) {
            this.toastr.error('No requests found in session data', 'Error');
          } else {
            this.session = res.session!;
            this.requests = res.requests;
            this.providers = res.providers ?? [];
            // A request-id search lands on that request, not the session's first.
            const matched = res.matchedRequestId
              ? res.requests.find((r) => r.RequestID === res.matchedRequestId)
              : undefined;
            if (res.matchedRequestId && !matched) {
              this.toastr.warning(
                `Request ${res.matchedRequestId} is beyond the requests shown for its session`,
                'Request not shown',
              );
            }
            this.selectReq(matched);
          }
        },
        error: (error) => {
          // Neither a session nor a request id the tracer holds for this
          // user: the empty state says so, it is not a failure.
          if (isNotFound(error)) return;
          // Network/HTTP error path — keep the user on /search (see
          // note above) so the URL and a reload-to-retry remain
          // available without manual re-typing the session ID.
          this.toastr.error('Failed to load session data', 'Error');
        },
      });
  }

  changeIsImportant() {
    if (this.importantPending) return;
    this.importantPending = true;
    const previous = this.session.important;
    this.session.important = !previous;
  }

  /** Identifies one download link, so only its spinner turns. */
  zipKey(filter: SessionZipFilter = {}): string {
    return `${filter.provider ?? '*'}|${filter.scope ?? 'all'}`;
  }

  /**
   * Download the session as a zip — v1's session zip. With no filter, every
   * request and airline call in order, with headers and a summary.json of UTC
   * times; with a provider and/or scope 'airline', what to send one airline.
   */
  downloadSessionZip(filter: SessionZipFilter = {}): void {
    const sessionId = this.session?.value;
    if (!sessionId || this.zipDownloading) return;
    this.zipDownloading = this.zipKey(filter);
    const suffix = (filter.provider ? `-${filter.provider}` : '') + (filter.scope ? `-${filter.scope}` : '');
    this.tracerService
      .downloadSessionZip(sessionId, filter)
      .pipe(
        timeout(10 * 60 * 1000),
        finalize(() => (this.zipDownloading = '')),
      )
      .subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = `tracer-session-${sessionId}${suffix}.zip`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        },
        error: (err) => {
          this.toastr.error(
            err instanceof TimeoutError
              ? 'Timed out downloading the session'
              : 'Failed to download the session',
            'Error',
          );
        },
      });
  }

  /** When our answer went back: the request's start plus its duration. */
  responseAt(req?: Request): string {
    return completedAt(req?.CreatedAt, req?.DurationMs);
  }

  selectReq(selReq?: Request): void {
    const reqId = this.route.snapshot.queryParamMap.get('req');
    const traceId = this.route.snapshot.queryParamMap.get('trace');

    this.selectedTrace = null;
    this.selectedMessage = null;

    // Set selectedReq based on query param
    if (selReq) {
      this.selectedReq = selReq;
    } else if (!this.selectedReq && reqId) {
      const matched = this.requests.find((item) => item.ID === Number(reqId));
      if (matched) {
        this.selectedReq = matched;
      }
    } else {
      this.selectedReq = this.requests[0];
    }
    this.env = this.traceUtilsService.getEnv(this.selectedReq);
    this.hash = this.traceUtilsService.getHash(this.selectedReq);

    this.env = this.traceUtilsService.getEnv(this.selectedReq);
    this.hash = this.traceUtilsService.getHash(this.selectedReq);

    if (this.selectedReq) {
      let traceForSelection: any;

      if (this.shouldTracesBeGrouped) {
        this.sortUniqueTraces();

        traceForSelection =
          this.uniqueTracesByProviders.find((trace) => isInboundTraceSource(trace.Source)) ||
          this.uniqueTracesByProviders[0];
      } else {
        traceForSelection = this.selectedReq.Traces?.[0];
      }

      if (!this.selectedTrace) {
        this.selectTrace(traceForSelection);
      } else if (traceId) {
        const found = this.selectedReq.Traces.find((item) => item.ID === Number(traceId));
        if (found) {
          this.selectTrace(found);
        }
      }
    }
  }

  selectTrace(trace: Trace): void {
    const msg = this.route.snapshot.queryParamMap.get('msg');

    this.selectedMessage = null;
    this.selectedTrace = trace;

    if (!this.selectedMessage) {
      // Deep-link from /diff carries ?payload=input|output|… — when
      // present and matched, land directly on that message so the
      // operator doesn't have to click through tabs.
      const deepLinked = this.diffSourcePayload
        ? this.selectedTrace?.Messages?.find((m) => m.Name === this.diffSourcePayload)
        : undefined;
      // Auto-select the request half. Backend orders by created_at ASC,
      // which usually puts input first, but raw/sidecar timing can flip
      // it. Pick by name so the operator always lands on the request
      // (input / provider_request) — same canonical order the tab pipe
      // uses for the rendered tabs.
      const first =
        deepLinked ??
        this.selectedTrace?.Messages?.find((m) => REQUEST_NAMES.has(m.Name)) ??
        this.selectedTrace?.Messages?.[0];
      this.selectMessage(first);
    } else if (msg) {
      const matchingMsg = this.selectedTrace?.Messages?.find((item: any) => item.ID === msg);
      if (matchingMsg) {
        this.selectMessage(matchingMsg);
      }
    }
  }

  selectMessage(message: Message): void {
    this.body = '';
    this.rawBody = '';
    this.headers = '';
    this.bodyFromPresignedURL = '';

    this.copiedHeaders = false;
    this.copiedBody = false;
    this.showHeaders = false;
    this.fetchMessageDetailsError = '';
    this.fetchMessageDetailsWarning = '';
    // Reset file-size tier state — each message gets its own classification
    // back from the backend on /message/:id.
    this.selectedMessageSize = 0;
    this.selectedMessageZipOnly = false;
    this.onDemandPresignedURL = '';
    this.zipOnlyDownloadURL = '';
    this.selectedMessage = message;
    // v2 carries headers on the message itself, independent of the body.
    this.headers = message?.Headers ?? '';
    const selectedMessageId = this.selectedMessage?.ID;

    if (
      selectedMessageId &&
      !this.cachedBodies[selectedMessageId] &&
      (message.Name === 'output' || message.Name === 'provider_response')
    ) {
      this.showFetchMessageDetailsButton = true;
    } else {
      this.fetchMessageDetails();
    }
  }

  fetchMessageDetails(): void {
    const messageId = this.selectedMessage?.ID;
    // v2 addresses bodies by URL, not numeric id — the API hands one back on
    // every message. Keep messageId for the local body/header caches.
    const bodyUrl = this.selectedMessage?.BodyUrl;
    // Checked before the spinner starts: returning after it left the page
    // loading forever.
    if (messageId == null || !bodyUrl) {
      this.showFetchMessageDetailsButton = false;
      if (this.selectedMessage) this.fetchMessageDetailsError = 'No body stored for this message.';
      return;
    }

    this.isMessageLoading = true;
    this.showFetchMessageDetailsButton = false;
    this.showDownloadButton = false;
    this.spinner.show();

    if (this.cachedBodies[messageId] !== undefined) {
      this.body = this.cachedBodies[messageId];
      this.selectedMessageExceeded = this.body.length > this.mbSize;
      this.isMessageLoading = false;
      this.showDownloadButton = true;
      return;
    }

    this.tracerService
      .getMessage(bodyUrl)
      .pipe(
        timeout(60000),
        finalize(() => {
          this.isMessageLoading = false;
          this.showDownloadButton = true;
          this.spinner.hide();
        }),
      )
      .subscribe({
        next: (response) => {
          // Capture file size up front — useful for both the on-demand
          // and zip-only tiers' user-facing messages, and rendered next
          // to the message in the UI for any tier that has it.
          this.selectedMessageSize = (response?.size as number) || 0;

          // Tier 3: file is above onDemandDisplayLimit. Backend returns
          // {size, zip_only: true, warning, max_*}. No presigned URL —
          // operator must use the session-ZIP download flow.
          if (response?.zip_only) {
            this.selectedMessageZipOnly = true;
            // Keep the presigned URL (download-only, NOT shown inline) so the
            // Download File button can fetch the real bytes on demand instead
            // of saving an empty file.
            this.zipOnlyDownloadURL = (response.presigned_url as string) || '';
            this.fetchMessageDetailsWarning =
              (response.warning as string) ||
              `This file is ${this.humanBytes(this.selectedMessageSize)}. Use the session ZIP download to access it.`;
            return;
          }

          // Tier 2: file is between inlineDisplayLimit and
          // onDemandDisplayLimit. Backend returns {presigned_url, size,
          // display_on_demand: true}. Stash the URL and surface a
          // "Show file (N KB)" button — DON'T auto-fetch. Saves
          // bandwidth + brotli cycles when operators are scanning.
          if (response?.display_on_demand && response?.presigned_url) {
            this.onDemandPresignedURL = response.presigned_url as string;
            this.fetchMessageDetailsWarning = `This file is ${this.humanBytes(this.selectedMessageSize)}. Click "Show file" below to load it.`;
            return;
          }

          // Backwards-compat: backend may return just `presigned_url`
          // without the new flags (older deployments). Treat as
          // on-demand to preserve the new behavior — operator gets
          // the button rather than an automatic fetch.
          if (response?.presigned_url) {
            this.onDemandPresignedURL = response.presigned_url as string;
            this.fetchMessageDetailsWarning = `This file is too large to display inline. Click "Show file" below to load it.`;
            return;
          }

          // Tier 1: inline body+headers returned. Format and display.
          if (response) {
            if (response.body) {
              // Format follows the content; it also drives the syntax
              // highlighting and the download extension.
              const format = sniffBodyFormat(response.body);
              if (this.selectedMessage) this.selectedMessage.Format = format;
              const body =
                format === 'xml'
                  ? this.formattedXML(response.body)
                  : this.formattedJSON(response.body);

              // Keep the original payload before pretty-printing so
              // copyCurl can emit the bytes that were actually sent.
              this.rawBody = response.body;
              this.body = body;
              this.cachedBodies[messageId] = body;
              this.selectedMessageExceeded = this.body.length > this.mbSize;
            } else if (response.error) {
              this.fetchMessageDetailsError = response.error;
            }
          } else {
            this.fetchMessageDetailsError = 'Response is empty.';
          }
        },
        error: (err) => {
          this.fetchMessageDetailsError =
            err instanceof TimeoutError
              ? 'Not possible to load.'
              : 'An error occurred while fetching the message.';
        },
      });
  }

  formattedXML(xml: string): string {
    if (this.selectedMessage?.Name === 'response' || xml.trim().charAt(0) !== '<') {
      return xml;
    }

    const itemsLength = xml.split(/(\r\n|\n|\r)/gm).length;
    const processedXMLString = xml.replace(/(\r\n|\n|\r)/gm, '');

    return processedXMLString.trim() !== xml.trim() && itemsLength > 2
      ? xml
      : xmlFormat(xml, { indentation: '  ', collapseContent: true });
  }

  formattedJSON(json: string): string {
    // Pretty-print whenever the body actually parses as JSON. Some
    // capture paths (notably gzip-decoded sidecar bodies) hand the
    // viewer a single-line minified payload — without this it
    // rendered as one wrapped line in the highlight.js fallback,
    // since the JSON-viewer branch only kicks in for already-valid
    // input. Two-space indent matches the XML pretty-print style
    // immediately above and the backend's json.Indent.
    //
    // If parse fails we hand the raw string back untouched so the
    // viewer can still surface non-JSON bodies (errors, empty
    // responses, malformed payloads) instead of swallowing them.
    if (!json) return json;
    try {
      return JSON.stringify(JSON.parse(json), null, 2);
    } catch {
      return json;
    }
  }

  copyHeaders(): void {
    let str = '';

    for (const key of Object.keys(this.headers)) {
      str += `${key}: ${this.headers[key]}\n`;
    }

    this.copyToClipboard(str, 'copiedHeaders');
  }

  copyCurl(): void {
    const endpoint = `${runtimeConfig.apiUrl}/v1.2/${this.selectedReq.Method}`;
    const rawBody = '  --data-raw';
    const splitter = ' \\\n';

    const headersToAvoid = ['content-length', 'ag-request-id', 'ag-session-id'];

    const headersString = Object.entries(this.headers)
      .filter(([key]) => !headersToAvoid.includes(key.toLowerCase()))
      .map(([key, value]) => `  -H '${key.toLowerCase()}: ${value}'`)
      .join(splitter);

    // Use the original, un-reformatted body so the request replays the
    // exact bytes that were sent. Fall back to the display body when the
    // raw value wasn't captured (e.g. an older cached entry).
    const dataRaw = this.rawBody || this.body;

    const curlCommand = `curl '${endpoint}'${splitter}${headersString}${splitter}${rawBody} '${dataRaw}'${splitter}--compressed`;

    this.copyToClipboard(curlCommand, 'copiedCurl');
  }

  copyCode(): void {
    this.copyToClipboard(this.body, 'copiedBody');
  }

  // Copies text to the clipboard via the async Clipboard API. Only flips
  // the matching "copied" flag (and schedules its 5s reset) when the
  // write actually succeeds; on failure it surfaces a toast and leaves
  // the flag untouched so the button never falsely reports success.
  private copyToClipboard(text: string, flag: 'copiedHeaders' | 'copiedBody' | 'copiedCurl'): void {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this[flag] = true;
        setTimeout(() => {
          this[flag] = false;
        }, 5000);
      })
      .catch(() => {
        this.toastr.error('Failed to copy to clipboard', 'Error');
      });
  }

  // humanBytes formats a byte count to match the backend's
  // page_controller.go:humanBytes ("250 KB", "1 MB", "1.4 MB").
  // Decimal-power so the displayed numbers match the threshold
  // constants (inlineDisplayLimit = 250 * 1024 → "250 KB",
  // onDemandDisplayLimit = 1024 * 1024 → "1 MB").
  humanBytes(n: number): string {
    if (!n || n < 0) return '0 B';
    const KB = 1024;
    const MB = 1024 * 1024;
    if (n >= MB) {
      const v = n / MB;
      const s = v.toFixed(1);
      return (s.endsWith('.0') ? s.slice(0, -2) : s) + ' MB';
    }
    if (n >= KB) {
      return `${Math.floor(n / KB)} KB`;
    }
    return `${n} B`;
  }

  // loadOnDemandFile fetches the Tier-2 file from S3 directly,
  // decompresses brotli in-browser, and assigns to body/headers so
  // it renders in the viewer panel. Triggered by the "Show file"
  // button surfaced when /message/:id returned display_on_demand=true.
  // The presigned URL is single-use enough that we clear it after
  // a successful fetch — re-clicking re-runs the full message
  // selection flow.
  loadOnDemandFile(): void {
    if (!this.onDemandPresignedURL) return;
    this.isMessageLoading = true;
    this.fetchMessageDetailsWarning = '';
    this.spinner.show();
    this.tracerService
      .getMessageFromPresignedUrl(this.onDemandPresignedURL)
      .pipe(
        timeout(60000),
        finalize(() => {
          this.isMessageLoading = false;
          this.spinner.hide();
        }),
      )
      .subscribe({
        next: async (data) => {
          const [headers, bodyEncoded] = data.split('%DELIM%');
          // decodeMessageBody handles both layers in order: strips
          // brotli_ + base64 storage wrapper, then ungzips when the
          // inner payload's first two bytes are the gzip magic 1f 8b
          // (typical for provider responses captured by the sidecar
          // with Content-Encoding: gzip preserved verbatim). Pre-fix
          // the gzip layer was treated as UTF-8 text, producing the
          // 213k-replacement-character garbage seen on AirShopping
          // outputs in the sandbox 2026-05-01 reports.
          const body = await decodeMessageBody(bodyEncoded);
          if (body) {
            // Format matches the inline-tier path so the viewer renders
            // identically once the body is loaded.
            const format = sniffBodyFormat(body);
            if (this.selectedMessage) this.selectedMessage.Format = format;
            const formatted =
              format === 'xml' ? this.formattedXML(body) : this.formattedJSON(body);
            // Original decoded payload, before pretty-printing — used
            // by copyCurl so --data-raw matches what was sent.
            this.rawBody = body;
            this.body = formatted;
            this.headers = this.parseHeadersBlob(headers);
            // Cache so re-clicking the same message in this session is instant.
            const messageId = this.selectedMessage?.ID;
            if (messageId) {
              this.cachedBodies[messageId] = formatted;
              this.cachedHeaders[messageId] = this.headers;
            }
            this.bodyFromPresignedURL = `${headers}\n\n${body}`; // keep for Download button fallback
            this.onDemandPresignedURL = '';
            this.selectedMessageExceeded = this.body.length > this.mbSize;
          } else {
            this.fetchMessageDetailsError = 'Error fetching message.';
          }
        },
        error: (err) => {
          this.fetchMessageDetailsError =
            err instanceof TimeoutError ? 'Not possible to load.' : 'Error fetching message.';
        },
      });
  }

  // parseHeadersBlob converts the "Key: value\nKey: value" block
  // emitted before the %DELIM% in presigned-URL responses into the
  // {key: value} map shape the inline-tier code path produces.
  // Lets the viewer's headers panel render the same way regardless
  // of which tier loaded the body.
  private parseHeadersBlob(blob: string): { [key: string]: string } {
    const out: { [key: string]: string } = {};
    if (!blob) return out;
    for (const line of blob.split('\n')) {
      const colon = line.indexOf(':');
      if (colon < 0) continue;
      const k = line.slice(0, colon).trim();
      const v = line.slice(colon + 1).trim();
      if (k) out[k] = v;
    }
    return out;
  }

  async downloadFile(): Promise<void> {
    // Prefer content already in memory (Tier 1 inline, or Tier 2 once the
    // user clicked "Show file").
    let content = this.bodyFromPresignedURL !== '' ? this.bodyFromPresignedURL : this.body;

    // Tier 3 (zip-only): nothing was loaded into the viewer — rendering
    // multi-MB inline is the slow path we avoid — but the user still wants
    // the real file. Fetch it from the presigned URL and decode the storage
    // wrapper (brotli + optional gzip) WITHOUT rendering it in the DOM, so
    // the saved file holds the actual content instead of being empty.
    if (!content && this.zipOnlyDownloadURL) {
      this.isMessageLoading = true;
      this.spinner.show();
      try {
        const data = await firstValueFrom(
          this.tracerService
            .getMessageFromPresignedUrl(this.zipOnlyDownloadURL)
            .pipe(timeout(120000)),
        );
        const [, bodyEncoded] = (data as string).split('%DELIM%');
        content = await decodeMessageBody(bodyEncoded);
      } catch (err) {
        this.toastr.error(
          err instanceof TimeoutError ? 'Timed out downloading file' : 'Failed to download file',
          'Error',
        );
        return;
      } finally {
        this.isMessageLoading = false;
        this.spinner.hide();
      }
    }

    if (!content) {
      this.toastr.error('No content available to download', 'Error');
      return;
    }

    const ext = this.downloadExtension(content);
    const blob = new Blob([content], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = `${this.session.value}_${this.selectedTrace?.Providers}_${this.selectedMessage?.Name}.${ext}`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  // downloadExtension picks the file extension for the saved download. The
  // stored Format is authoritative when it's a real content type
  // (xml/json), but the sidecar leg often stores Format="raw" — saving as
  // ".raw" is meaningless to the user. For that (and any other non-content
  // value) we sniff the decoded payload so they get .json / .xml.
  private downloadExtension(content: string): string {
    const fmt = this.selectedMessage?.Format;
    if (fmt === 'xml' || fmt === 'json') {
      return fmt;
    }
    const trimmed = content.trimStart();
    if (trimmed.startsWith('<')) {
      return 'xml';
    }
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      return 'json';
    }
    return fmt || 'txt';
  }

  sortUniqueTraces(): void {
    const traces = this.selectedReq.Traces;
    if (!traces) return;

    this.uniqueTracesByProviders = traces
      .filter((item, index, self) => {
        const allTracesByProviders = self.filter(
          (it) => it.Providers === item.Providers && it.Source === item.Source,
        );

        if (allTracesByProviders.every((t) => t.Status === 'error')) {
          item.Order = 3;
        } else if (allTracesByProviders.some((t) => t.Status === 'error')) {
          item.Order = 2;
        } else {
          item.Order = 1;
        }

        return (
          self.findIndex((t) => t.Providers === item.Providers && t.Source === item.Source) ===
          index
        );
      })
      .sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0));
  }

  toggleGroup(traceId: number) {
    if (this.expandedGroups.has(traceId)) {
      this.expandedGroups.delete(traceId);
    } else {
      this.expandedGroups.clear();
      this.expandedGroups.add(traceId);
    }
  }

  get shouldTracesBeGrouped(): boolean {
    return this.selectedReq?.Method === 'AirShopping' || this.selectedReq?.Method === 'OrderReshop';
  }

  // First AirShopping request in the session — the "shop" that opened
  // the flow. Drives whether the Relaunch-in-BP dropdown is shown and
  // which request's RQ is replayed into BookingPad.
  get airShoppingRequest(): Request | undefined {
    return this.requests?.find((r) => r.Method === 'AirShopping');
  }

  // Local BookingPad base URL built from the operator-configured port
  // (persisted per-browser; defaults to :4201). Used by the dropdown's
  // Localhost row.
  get bpLocalhostBase(): string {
    return `http://localhost:${this.bpLocalPort}/`;
  }

  // Persist a new local BP dev port from the dropdown's inline input.
  // Validates 1–65535; on invalid input it toasts and reverts the
  // field to the last good value.
  onBpPortChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value.trim();
    const n = Number(raw);
    if (!raw || !Number.isInteger(n) || n < 1 || n > 65535) {
      this.toastr.error('Enter a valid port (1–65535).');
      input.value = this.bpLocalPort;
      return;
    }
    this.bpLocalPort = String(n);
    this.ls.setItem(BP_LOCAL_PORT_KEY, this.bpLocalPort);
  }

  // Open this session's AirShopping search in the chosen BookingPad
  // environment. Locates the AirShopping request's inbound `input`
  // message, fetches its body (uses the cache if already viewed),
  // rebuilds the BP /search query from the RQ, and opens it in a new
  // tab. `base` is one of BP_TARGETS[].base.
  relaunchInBP(base: string): void {
    const req = this.airShoppingRequest;
    if (!req) {
      this.toastr.error('No AirShopping request found in this session.');
      return;
    }
    const inboundTrace = req.Traces?.find((t) => isInboundTraceSource(t.Source)) ?? req.Traces?.[0];
    const inputMsg = inboundTrace?.Messages?.find((m) => m.Name === 'input');
    if (!inputMsg) {
      this.toastr.error('AirShopping request has no input message to replay.');
      return;
    }

    const openBp = (rawBody: string) => {
      let parsed: any;
      try {
        parsed = JSON.parse(rawBody);
      } catch {
        this.toastr.error('Could not parse the AirShopping request body.');
        return;
      }
      const query = this.buildBpSearchQuery(parsed, inboundTrace?.Providers, req.Agency);
      const qsStr = Object.keys(query).length ? '?' + qs.stringify(query) : '';
      window.open(base + 'search' + qsStr, '_blank', 'noopener');
    };

    const cached = this.cachedBodies[inputMsg.ID];
    if (cached !== undefined) {
      openBp(cached);
      return;
    }

    this.relaunchPending = true;
    this.spinner.show();
    this.tracerService
      .getMessage(inputMsg.BodyUrl ?? '')
      .pipe(
        timeout(60000),
        finalize(() => {
          this.relaunchPending = false;
          this.spinner.hide();
        }),
      )
      .subscribe({
        next: (response) => {
          if (response?.zip_only || response?.presigned_url || !response?.body) {
            this.toastr.error(
              'AirShopping request body is not available inline — cannot build the BookingPad link.',
            );
            return;
          }
          openBp(response.body as string);
        },
        error: (err) => {
          this.toastr.error(
            err instanceof TimeoutError
              ? 'Timed out fetching the AirShopping request body.'
              : 'Failed to fetch the AirShopping request body.',
          );
        },
      });
  }

  // Rebuilds BookingPad's /search query from an AirShopping RQ body.
  // Faithful restore of the original getQueryParams (commit 5bddebd)
  // so the produced URL stays byte-identical to what BP /search
  // already parses; only the inputs are now passed in (session-level)
  // rather than read from the currently-selected trace/request.
  private buildBpSearchQuery(body: any, providers?: string, agency?: string): Record<string, any> {
    const result: any = {};

    // Trip type from the origin-destination shape.
    if (body.originDestinations?.length === 1) {
      result.type = 'OW';
    } else if (
      body.originDestinations?.length === 2 &&
      body.originDestinations[0].departure.airportCode ===
        body.originDestinations[1].arrival.airportCode
    ) {
      result.type = 'RT';
    } else {
      result.type = 'MC';
    }

    result.cabin =
      body.preferences && body.preferences.cabin && body.preferences.cabin.length
        ? body.preferences.cabin[0]
        : '7'; // 7 = All Cabins

    if (body.travelers) {
      for (const [key, value] of Object.entries(body.travelers)) {
        if (value) {
          result['travelers_' + key.toLowerCase()] = value;
        }
      }
    } else if (body.passengers && body.passengers.length) {
      body.passengers.reduce((counts: any, passenger: any) => {
        const k = 'travelers_' + passenger.passengerType.toLowerCase();
        counts[k] = (counts[k] || 0) + 1;
        return counts;
      }, result);
    } else {
      result['travelers_adt'] = 1;
    }

    body.originDestinations?.forEach((od: any, index: number) => {
      result['origin_destinations_dep' + `[${index}]`] = od.departure.airportCode;
      result['origin_destinations_arr' + `[${index}]`] = od.arrival.airportCode;
      result['origin_destinations_date' + `[${index}]`] = od.departure.date;
      if (index === body.originDestinations.length - 1) {
        result['idx'] = index;
      }
    });

    if (providers) {
      const providerPattern = /^([A-Z0-9]{2,})(,[A-Z0-9]{2,})*$/;
      if (providerPattern.test(providers)) {
        result['providers'] = providers;
      }
    }

    if (agency) {
      result['agency'] = agency;
    }

    return result;
  }

  openTicket(): void {
    this.showTicketPanel = true;
  }

  closeTicket(): void {
    this.showTicketPanel = false;
  }

  openReplay(): void {
    this.showReplayPanel = true;
  }

  closeReplay(): void {
    this.showReplayPanel = false;
  }

  openCompare(): void {
    this.showDiffInputModal = true;
  }

  closeCompare(): void {
    this.showDiffInputModal = false;
  }

  /**
   * The distinct NDC methods this request actually sent to airlines.
   *
   * Read off the outbound legs rather than a field on the request: one inbound
   * request can produce several (a provider Login followed by the real call,
   * or a fan-out where sequential calls differ), so a single value would be a
   * lie. Returns an em dash when nothing went out.
   */
  providerMethodsFor(req: Request): string {
    const methods = (req?.Traces ?? [])
      .filter((t) => t.Source === 'requester' && t.ProviderMethod)
      .map((t) => t.ProviderMethod as string);
    const unique = [...new Set(methods)];
    return unique.length ? unique.join(', ') : '—';
  }

  /** True when the page was opened from the /diff page with deep-link params. */
  get cameFromDiff(): boolean {
    return !!this.diffSourcePath && !!this.diffSourcePayload;
  }
}
